import { useMemo } from 'react';
import { EventType, TimelineEvent } from '@ais-ui/ui-core';
import { v4 as uuidv4 } from 'uuid';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { DateRange } from 'react-day-picker';
import type { Inspection, Ban, Sanction } from './VesselIntegrityCards';
import type { PopupVesselTimelineData } from './popupVesselTypes';

interface UseVesselTimelineEventsParams {
  data: PopupVesselTimelineData | null;
  dateRange: DateRange | undefined;
}

interface UseVesselTimelineEventsResult {
  eventTypes: EventType[];
  allEvents: TimelineEvent[];
  filteredEvents: TimelineEvent[];
}

export function useVesselTimelineEvents({
  data,
  dateRange,
}: UseVesselTimelineEventsParams): UseVesselTimelineEventsResult {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  const eventTypes: EventType[] = useMemo(
    () => [
      { id: 'port-call', name: 'Port Call', color: '#3B82F6', disabled: false },
      {
        id: 'inspection',
        name: 'Inspection',
        color: '#ff6e00',
        disabled: !data?.permissions?.mdmIntegrity,
      },
      {
        id: 'ban',
        name: 'Ban',
        color: '#311B92',
        disabled: !data?.permissions?.mdmIntegrity,
      },
      {
        id: 'sanction',
        name: 'Sanction',
        color: '#880E4F',
        disabled: !data?.permissions?.mdmIntegrity,
      },
    ],
    [data?.permissions?.mdmIntegrity],
  );

  const portCallEvents: TimelineEvent[] = useMemo(() => {
    if (!data?.portCalls) return [];

    const portCallType = eventTypes.find((t) => t.id === 'port-call')!;

    return data.portCalls.map((portCall, i) => {
      const portName = portCall.port?.name || 'Unknown';
      const locode = portCall.port?.locode || '';
      const ata = portCall.ata ? new Date(portCall.ata) : null;
      const atd = portCall.atd ? new Date(portCall.atd) : null;

      let durationStr = '—';
      if (ata && atd) {
        const durationMs = atd.getTime() - ata.getTime();
        const days = Math.floor(durationMs / (24 * 3600 * 1000));
        const hours = Math.floor(
          (durationMs % (24 * 3600 * 1000)) / (3600 * 1000),
        );
        const minutes = Math.floor((durationMs % (3600 * 1000)) / (60 * 1000));
        const parts = [];
        if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
        if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
        if (minutes > 0)
          parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
        durationStr =
          parts.length > 0 ? parts.join(', ') : 'less than a minute';
      }

      return {
        id: `port-call-${i}`,
        date: ata || new Date(),
        title: portName,
        description: atd
          ? `Arrived at ${portName} at ${formatDateTimeWithTimeZone(ata)} and departed at ${formatDateTimeWithTimeZone(atd)} for a total of ${durationStr}.`
          : `Arrived at ${portName} at ${formatDateTimeWithTimeZone(ata)}, currently in port.`,
        type: portCallType,
        ongoing: !atd,
        metadata: {
          LOCODE: locode,
          Arrival: formatDateTimeWithTimeZone(ata),
          Departure: atd ? formatDateTimeWithTimeZone(atd) : '—',
          Duration: durationStr,
        },
      };
    });
  }, [data?.portCalls, eventTypes, formatDateTimeWithTimeZone]);

  const inspectionEvents: TimelineEvent[] = useMemo(() => {
    if (
      !data?.permissions?.mdmIntegrity ||
      !data?.advancedIntegrity?.inspections
    )
      return [];
    const inspectionType = eventTypes.find((t) => t.id === 'inspection')!;

    return data.advancedIntegrity.inspections.map((inspection: Inspection) => {
      const formattedType =
        inspection.inspection_type
          ?.split('_')
          .map((word: string) => word.charAt(0) + word.slice(1).toLowerCase())
          .join(' ') || 'Inspection';

      let description = `${formattedType} conducted at ${inspection.inspection_port}, ${inspection.inspection_country}.`;
      if (inspection.deficiency_count > 0) {
        description += ` Found ${inspection.deficiency_count} deficiencies.`;
        if (inspection.deficiency_ground_for_detention_count > 0) {
          description += ` ${inspection.deficiency_ground_for_detention_count} were grounds for detention.`;
        }
      } else {
        description += ' No deficiencies were found.';
      }

      return {
        id: `inspection-${uuidv4()}`,
        date: new Date(inspection.inspection_date),
        title: `${formattedType} - ${inspection.inspection_port}`,
        description,
        type: inspectionType,
      };
    });
  }, [
    data?.advancedIntegrity?.inspections,
    data?.permissions?.mdmIntegrity,
    eventTypes,
  ]);

  const banEvents: TimelineEvent[] = useMemo(() => {
    if (!data?.permissions?.mdmIntegrity || !data?.advancedIntegrity?.bans)
      return [];
    const banType = eventTypes.find((t) => t.id === 'ban')!;

    return data.advancedIntegrity.bans.map((ban: Ban) => {
      let description = `Ban issued by ${ban.ban_country_of_inspection} for ${ban.ban_reason}.`;
      if (ban.ban_end_date) {
        description += ` The ban ended on ${formatDateTimeWithTimeZone(new Date(ban.ban_end_date))}.`;
      } else {
        description += ` The ban is currently ${ban.ban_status.toLowerCase()}.`;
      }
      if (ban.related_psc) {
        description += ` Related to PSC reference: ${ban.related_psc}.`;
      }

      return {
        id: `ban-${uuidv4()}`,
        date: new Date(ban.ban_begin_date),
        title: `Ban - ${ban.ban_country_of_inspection}`,
        description,
        type: banType,
        metadata: {
          'Ban Country': ban.ban_country_of_inspection,
          'Begin Date': formatDateTimeWithTimeZone(
            new Date(ban.ban_begin_date),
          ),
          'End Date': ban.ban_end_date
            ? formatDateTimeWithTimeZone(new Date(ban.ban_end_date))
            : 'Ongoing',
          Status: ban.ban_status,
          Reason: ban.ban_reason,
        },
      };
    });
  }, [
    data?.advancedIntegrity?.bans,
    data?.permissions?.mdmIntegrity,
    eventTypes,
    formatDateTimeWithTimeZone,
  ]);

  const sanctionEvents: TimelineEvent[] = useMemo(() => {
    if (!data?.permissions?.mdmIntegrity || !data?.advancedIntegrity?.sanctions)
      return [];
    const sanctionType = eventTypes.find((t) => t.id === 'sanction')!;

    return data.advancedIntegrity.sanctions.map((sanction: Sanction) => {
      let description = `${sanction.sanction_type} sanction imposed by ${sanction.sanction_by} under ${sanction.program} program.`;
      if (sanction.reason) {
        description += ` Reason: ${sanction.reason}.`;
      }
      if (sanction.sanction_end_date) {
        description += ` The sanction ended on ${formatDateTimeWithTimeZone(new Date(sanction.sanction_end_date))}.`;
      } else {
        description += ` The sanction is currently active.`;
      }

      return {
        id: `sanction-${uuidv4()}`,
        date: new Date(sanction.sanction_begin_date),
        title: `Sanction - ${sanction.sanction_by}`,
        description,
        type: sanctionType,
        metadata: {
          'Imposed By': sanction.sanction_by,
          Program: sanction.program,
          Type: sanction.sanction_type,
          'Begin Date': formatDateTimeWithTimeZone(
            new Date(sanction.sanction_begin_date),
          ),
          'End Date': sanction.sanction_end_date
            ? formatDateTimeWithTimeZone(new Date(sanction.sanction_end_date))
            : 'Ongoing',
          Reason: sanction.reason || 'Not specified',
        },
      };
    });
  }, [
    data?.advancedIntegrity?.sanctions,
    data?.permissions?.mdmIntegrity,
    eventTypes,
    formatDateTimeWithTimeZone,
  ]);

  const allEvents = useMemo(() => {
    return [
      ...portCallEvents,
      ...inspectionEvents,
      ...banEvents,
      ...sanctionEvents,
    ];
  }, [portCallEvents, inspectionEvents, banEvents, sanctionEvents]);

  const filteredEvents = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) return allEvents;
    return allEvents.filter((event) => {
      const eventDate = new Date(event.date);
      return eventDate >= dateRange.from! && eventDate <= dateRange.to!;
    });
  }, [allEvents, dateRange]);

  return { eventTypes, allEvents, filteredEvents };
}
